import { Injectable } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { BehaviorSubject, Observable } from 'rxjs';
import { LoggerService } from '../logger/logger.service';
import { ApiClientAccountOut, ClientRequestInput, ConsultView, DraftTemplateIn, GenerateDocumentLinkOut, PortfolioDepositOut, PortfolioFeeConfigsOut, PortfolioOut } from '../rest-api/rest-api';
import { RestApiService } from '../rest-api/rest-api.service';
import { ToastNotificationService } from '../toast-notification/toast-notification.service';
import { AcceptEfektikaDraftModalChaning, CancelRegularWithdrawal, CreateEfektikaStrategyInput, DeleteDraftTemplateModalConfirm, EfektikaDraftAcceptModalInput, EfektikaModalLoadingInput, MoneyDepositInput, NewRegularWithdrawal, OutdatedAmlClientInput, SaveStrategyTemplateCreatePayload, SaveStrategyTemplateModalInput } from './modal';
import { ToastNotificationComponent } from 'src/app/shared/toast-notification/toast-notification.component';
import { Router } from '@angular/router';
import * as saveAs from 'file-saver';
import { ExpectedOwnedStrategy } from 'src/app/shared/owned-strategy-card/strategy';
import { SignalsService } from '../signals.service';
import Decimal from 'decimal.js';
import { STEPPER_LIMITATIONS } from '../suggesting.service';
import { EfektikaConsultService } from '../efektika-consult.service';
import { AcceptEfektikaModalSource, AcceptEfektikaNextAction, EfektikaClientService } from '../efektika-client.service';
import { ConsultClientService } from '../consult-client.service';
import { DRAFT_TEMPLATES_CONFIG, DRAFT_TEMPLATES_ROUTE, INVESTMENT_GOAL_ROUTE, WITHDRAWAL_COMMENT_ROUTE } from 'src/app/shared/constants';
import { isInputError } from 'src/app/shared/functions';

export type ModalDisplay = 'close' | 'open';

@Injectable({
  providedIn: 'root'
})
export class ModalService {
  private display: BehaviorSubject<ModalDisplay> = new BehaviorSubject<ModalDisplay>('close');

  public preloader = false;
  public dipPreloader = false;
  public paymentInfoPreloader = false;
  public disableClose = false;
  public apiError = false;
  public currency: string | null = null;


  // used after close for route redirect or page reload
  private afterCloseRedirect: string | null = null;

  // modal type/content data - only one can be not-null!
  public moneyDeposit: MoneyDepositInput | null = null;
  public rebalancingControl: AbstractControl | null = null;
  public dipControl: AbstractControl | null = null;
  public dipOpenSource: 'new-strategy' | 'new-portfolio' | null = null;
  public outdatedAmlClient: OutdatedAmlClientInput | null = null;
  public preferedContractBankAccount: string | null = null;
  public investmentQuestionaireExpired: true | null = null;
  public rebalancingSwitchoffConfirm: true | null = null;
  public feeChangeConfirm: true | null = null;
  public feeConfigs: PortfolioFeeConfigsOut | null = null;
  public opfFeesConfirmClientId: number | null = null;
  public initSignIn: true | null = null;
  public consultCreateEfektikaStrategy: CreateEfektikaStrategyInput | null = null;
  public efektikaDraftAcceptConfirm: EfektikaDraftAcceptModalInput | null = null;
  public efektikaDraftAcceptDipConfirm: EfektikaModalLoadingInput | null = null;
  public acceptEfektikaDraftModalChaning: AcceptEfektikaDraftModalChaning | null = null;
  public efektikaDraftAcceptRegularExceedConfirm: EfektikaModalLoadingInput | null = null;
  public newPortfolioVatConfirm: boolean | null = null;
  public deleteTemplateDraftConfirm: DeleteDraftTemplateModalConfirm | null = null;
  public cancelRegularWithdrawal: CancelRegularWithdrawal | null = null;
  public newRegularWithdrawal: NewRegularWithdrawal | null = null;
  public transactionsExportForm: boolean | null = null;
  public saveStrategyTemplate: SaveStrategyTemplateModalInput | null = null;
  public saveStrategyTemplateSaving = false;

  public get containsNotNullData(): boolean {
    return !!this.rebalancingControl || !!this.moneyDeposit || !!this.dipControl || !!this.preferedContractBankAccount || !!this.investmentQuestionaireExpired || !!this.initSignIn || !!this.rebalancingSwitchoffConfirm || !!this.feeChangeConfirm || !!this.outdatedAmlClient || !!this.feeConfigs || !!this.consultCreateEfektikaStrategy || !!this.opfFeesConfirmClientId || !!this.newPortfolioVatConfirm || !!this.cancelRegularWithdrawal || !!this.newRegularWithdrawal || !!this.deleteTemplateDraftConfirm;
  }

  public constructor(
    private restApiService: RestApiService,
    public toastService: ToastNotificationService,
    private loggerService: LoggerService,
    private router: Router,
    private signalsService: SignalsService,
    private efektikaConsultService: EfektikaConsultService,
    private efektikaClientService: EfektikaClientService,
    private consultClientService: ConsultClientService,
  ) { }

  public watch(): Observable<string> {
    return this.display.asObservable();
  }

  public openMoneyDepositModalDirect(data: { deposit: PortfolioDepositOut, investmenDetail: PortfolioOut, consultView: ConsultView }): void {
    this.preloader = true;
    this.display.next('open');
    this.handleMoneyDepositData(data);
  }

  private handleMoneyDepositData(data: { deposit: PortfolioDepositOut, investmenDetail: PortfolioOut, consultView?: ConsultView }): void {
    this.moneyDeposit = {
      name: data.investmenDetail.name,
      accountNumber: data.deposit.accountNumber,
      variableSymbol: data.deposit.variableSymbol,
      strategy: data.investmenDetail.portfolioType === 'STRATEGY',
      portfolio: data.investmenDetail.portfolioType === 'PORTFOLIO',
      regularDeposit: data.deposit.regularDeposit,
      bankCode: data.deposit.bankCode,
      currency: data.deposit.currency,
      portfolioId: data.investmenDetail.portfolioId,
      dip: data.investmenDetail.dip,
      entryFeeAlreadyPaid: data.deposit.entryFeeInfo?.entryFeeAlreadyPaid === 0 ? 0 : (data.deposit.entryFeeInfo?.entryFeeAlreadyPaid ?? null),
      totalEntryFee: data.deposit.entryFeeInfo?.totalEntryFee ?? NaN,
      unpaidAmountWithVat: data.deposit.entryFeeInfo?.unpaidAmountWithVat ?? NaN,
      vatAlreadyPaid: data.deposit.entryFeeInfo?.vatAlreadyPaid ?? NaN,
      entryFeeAlreadyPaidWithVat: data.deposit.entryFeeInfo?.entryFeeAlreadyPaidWithVat ?? NaN,
      feeAlreadyPaid: Decimal.sub(data.deposit.entryFeeInfo?.entryFeeAlreadyPaidWithVat ?? 0, data.deposit.entryFeeInfo?.vatAlreadyPaid ?? 0).toNumber(),
      consultView: data.consultView,
    }
    this.preloader = false;
  }

  public openOutdatedAml(clientAccountId: number, suggesting: boolean): void {
    this.outdatedAmlClient = { clientAccountId, suggesting };
    this.display.next('open');
  }

  public navigateConsultClientToInvestmentGoal(): void {
    let route = '/draft-type-select';
    const alreadySelectedType = this.consultClientService.getDraftType();
    if(alreadySelectedType === 'investment') {
      route = '/' + INVESTMENT_GOAL_ROUTE;
    }
    this.router.navigate([route], {
      queryParams: {steps: this.outdatedAmlClient?.suggesting ? null : STEPPER_LIMITATIONS.STEPS_LIMITED},
    });
    this.close();
  }

  public openRebalancingModal(control: AbstractControl): void {
    this.rebalancingControl = control;
    this.display.next('open');
  }

  public confirmRebalancing(): void {
    this.rebalancingControl!.patchValue(true);
    this.close();
  }

  public openPortfolioFeesModal(params: ClientRequestInput, currency: string): void {
    this.currency = currency;
    this.restApiService.getPortfolioFees(params).subscribe({
      error: (error) => this.handlePortfolioFeesError(error),
      next: (data) => this.handlePortfolioFees(data),
    });
    this.preloader = true;
    this.display.next('open');
  }

  private handlePortfolioFees(data: PortfolioFeeConfigsOut): void {
    this.feeConfigs = data;
    this.apiError = false;
    this.preloader = false;
  }

  private handlePortfolioFeesError(error: unknown): void {
    this.preloader = false;
    this.apiError = true;
    this.loggerService.error(typeof error === 'function' ? error() : error);
    this.toastService.unknownApiError();
  }

  public openDipModal(control: AbstractControl, type: 'new-strategy' | 'new-portfolio'): void {
    this.dipControl = control;
    this.dipOpenSource = type;

    this.display.next('open');
  }

  public confirmDip(): void {
    this.dipControl!.patchValue(true);
    this.close();
  }

  public confirmDipEfektika(): void {
    // controlls that only one action will trigger and others are ignored
    const queueState = { open: true };
    this.triggerEfektikaModalActionIfPresent(AcceptEfektikaNextAction.MODAL_REGULAR_LIMIT_EXCEEDED_CONFIRMATION, queueState);
    this.triggerEfektikaCreateActionIfPresent(queueState, AcceptEfektikaModalSource.DIP);
  }

  public confirmRegularExceededEfektika(): void {
    // controlls that only one action will trigger and others are ignored
    const queueState = { open: true };
    this.triggerEfektikaCreateActionIfPresent(queueState, AcceptEfektikaModalSource.REGULAR_EXCEEDED);
  }

  private confirmEfektikaDraft(): void {
    // controlls that only one action will trigger and others are ignored
    const queueState = { open: true };
    this.triggerEfektikaModalActionIfPresent(AcceptEfektikaNextAction.MODAL_DIP_CONFIRMATION, queueState);
    this.triggerEfektikaModalActionIfPresent(AcceptEfektikaNextAction.MODAL_REGULAR_LIMIT_EXCEEDED_CONFIRMATION, queueState);
    this.triggerEfektikaCreateActionIfPresent(queueState, AcceptEfektikaModalSource.EFEKTIKA);
  }

  public close(forceRefreshRoute?: string): void {
    this.currency = null;
    this.apiError = false;
    this.preloader = false;

    this.rebalancingControl = null;
    this.dipControl = null;
    this.moneyDeposit = null;
    this.outdatedAmlClient = null;
    this.disableClose = false;
    this.preferedContractBankAccount = null;
    this.investmentQuestionaireExpired = null;
    this.feeConfigs = null;
    this.initSignIn = null;
    this.rebalancingSwitchoffConfirm = null;
    this.feeChangeConfirm = null;
    this.consultCreateEfektikaStrategy = null;
    this.opfFeesConfirmClientId = null;
    this.newPortfolioVatConfirm = null;
    this.cancelRegularWithdrawal = null;
    this.newRegularWithdrawal = null;
    this.transactionsExportForm = null;
    this.deleteTemplateDraftConfirm = null;
    this.saveStrategyTemplate = null;
    this.saveStrategyTemplateSaving = false;

    this.acceptEfektikaDraftModalChaning = null;

    this.display.next('close');
    this.signalsService.modalClose();

    const afterCloseRedirectRoute = this.afterCloseRedirect ?? forceRefreshRoute;
    if(afterCloseRedirectRoute) {
      this.afterCloseRedirect = null;
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.router.navigate([afterCloseRedirectRoute]);
      });
    }
  }

  public generatePaymentInfoPdf(): void {
    if(!this.moneyDeposit) {
      console.error('Undefined deposit object.')
      return;
    }

    this.paymentInfoPreloader = true;
    this.restApiService.generatePaymentInfoPdf({
      portfolioId: this.moneyDeposit.portfolioId,
      clientId: this.moneyDeposit.consultView?.clientId ?? NaN,
    }).subscribe({
      error: this.downloadPaymentInfoPdfError.bind(this),
      next: this.downloadPaymentInfoPdf.bind(this),
    });
  }

  private downloadPaymentInfoPdf(data: GenerateDocumentLinkOut): void {
    this.paymentInfoPreloader = false;
    const xhr = new XMLHttpRequest();
    xhr.open('GET', data.link, true);
    xhr.responseType = 'blob';

    xhr.onload = () => {
      if (xhr.status === 200) {
        const blob = new Blob([xhr.response], { type: 'application/pdf' });
        saveAs(blob, data.filename ?? 'Platebni_Údaje.pdf');
      } else {
        this.toastService.addNotification({
          title: 'toastNotifications.headers.error',
          message: 'toastNotifications.messages.documentDipError',
          type: 'warning',
          component: ToastNotificationComponent
        });
      }
    };

    xhr.send();
  }

  private downloadPaymentInfoPdfError(error: unknown): void {
    this.paymentInfoPreloader = false;
    this.loggerService.error(typeof error === 'function' ? error() : error);
    this.toastService.unknownApiError();
  }

  public generateDipPdf(): void {
    if(!this.moneyDeposit) {
      console.error('Undefined deposit object.')
      return;
    }

    this.dipPreloader = true;
    this.restApiService.employerPaymentPdf({
      portfolioId: this.moneyDeposit.portfolioId,
      clientId: this.moneyDeposit.consultView?.clientId ?? NaN,
    }).subscribe({
      error: this.handleDipPdfError.bind(this),
      next: this.downloadDipPdf.bind(this),
    });
  }

  private downloadDipPdf(data: {link: string}): void {
    this.dipPreloader = false;
    const xhr = new XMLHttpRequest();
    xhr.open('GET', data.link, true);
    xhr.responseType = 'blob';

    xhr.onload = () => {
      if (xhr.status === 200) {
        let fileName;

        try {
          fileName = xhr.getResponseHeader('content-disposition')?.split('\'\'')[1];
          if(!fileName) {
            throw new Error(`Failed to parse response when getting filename from ${xhr.getResponseHeader('content-disposition')}`);
          }
          fileName = decodeURI(fileName);
        } catch (error) {
          this.loggerService.error('Failed to parse filename from header. Using default name.', error)
        }

        const blob = new Blob([xhr.response], { type: 'application/pdf' });
        saveAs(blob, fileName ?? 'DIP_Platebni_Údaje.pdf');
      } else {
        this.toastService.addNotification({
          title: 'toastNotifications.headers.error',
          message: 'toastNotifications.messages.documentDipError',
          type: 'warning',
          component: ToastNotificationComponent
        });
      }
    };

    xhr.send();
  }

  private handleDipPdfError(error: unknown): void {
    this.dipPreloader = false;
    this.loggerService.error(typeof error === 'function' ? error() : error);
    this.toastService.unknownApiError();
  }

  public openInvestmentQuestionaireExpired(): void {
    this.disableClose = true;
    this.preloader = false;
    this.investmentQuestionaireExpired = true;
    this.display.next('open');
  }

  public openRebalancingSwitchoffConfirm(): void {
    this.rebalancingSwitchoffConfirm = true;
    this.display.next('open');
  }

  public openFeeChangeConfirm(): void {
    this.feeChangeConfirm = true;
    this.display.next('open');
  }

  public confirmRebalancingSwitchoff(): void {
    this.signalsService.confirmRebalancingSwitch();
    this.close();
  }

  public confirmFeesChange(): void {
    this.signalsService.confirmFeesChange();
    this.close();
  }

  // periodic check if mail-signed so modal can be closed
  private recheckUserDataAfterDelay(): void {
    setTimeout(() => {
      this.restApiService.getUserDetail().subscribe({
        error: this.handleSignPeriodicCheckError.bind(this),
        next: this.handleSignPeriodicCheckData.bind(this),
      });
    }, 15000);
  }

  private handleSignPeriodicCheckError(error: unknown): void {
    this.loggerService.error(typeof error === 'function' ? error() : error);
    this.disableClose = true;
    this.preloader = false;
    this.toastService.unknownApiError();
  }

  private handleSignPeriodicCheckData(userData: ApiClientAccountOut): void {
    const unassignedContract = userData.validContracts.find(contract => contract.unsignedAdditions.find((addition) => addition.type === 'MN_415'));
    if(!unassignedContract) {
      this.close();
    } else {
      this.recheckUserDataAfterDelay();
    }
  }

  public openConfirmConsultCreateEfektikaStrategy(input: CreateEfektikaStrategyInput): void {
    this.preloader = false;
    this.consultCreateEfektikaStrategy = input;
    this.display.next('open');
  }

  public confirmConsultCreatingEfektika(): void {
    this.preloader = false;
    const followsDipConfirmation = !!this.consultCreateEfektikaStrategy?.followsDipConfirmation;
    this.efektikaConsultService.triggerConfirmConsultCreatingEfektika();
    if (followsDipConfirmation) {
      this.consultCreateEfektikaStrategy = null;
    } else {
      this.close();
    }
  }

  public onAcceptEfektikaDraft(modalInput: EfektikaDraftAcceptModalInput | null): void {
    if (!modalInput || !modalInput.draftPortfolio || !modalInput.draftPortfolio.draftPortfolioId || !modalInput.draftPortfolio.efektika || !this.efektikaDraftAcceptConfirm || !this.acceptEfektikaDraftModalChaning) {
      this.toastService.addNotification({
        message: 'toastNotifications.messages.notEfektikaDraft',
        type: 'warning',
        component: ToastNotificationComponent
      });
      return;
    }

    this.confirmEfektikaDraft();
  }

  private triggerEfektikaModalActionIfPresent(action: AcceptEfektikaNextAction, queueState: { open: boolean }): void {
    if (!this.acceptEfektikaDraftModalChaning || !queueState.open) return;
    const { actions } = this.acceptEfektikaDraftModalChaning;

    if (actions.includes(action)) {
      this.efektikaClientService.triggerAcceptingEfektikaNextAction(action);
      this.efektikaDraftAcceptConfirm = null;
      queueState.open = false;
    }
  }

  private triggerEfektikaCreateActionIfPresent(queueState: { open: boolean }, modalSource: AcceptEfektikaModalSource): void {
    if (!this.acceptEfektikaDraftModalChaning || !queueState.open) return;
    const { actions, draftPortfolio } = this.acceptEfektikaDraftModalChaning;
    if (
      actions.includes(AcceptEfektikaNextAction.CREATE_REQUEST) &&
      draftPortfolio
    ) {
      this.efektikaClientService.triggerAcceptingEfektikaNextAction(
        AcceptEfektikaNextAction.CREATE_REQUEST, this.acceptEfektikaDraftModalChaning.draftPortfolio.draftPortfolioId
      );
      if (modalSource === AcceptEfektikaModalSource.EFEKTIKA && this.efektikaDraftAcceptConfirm) {
        this.efektikaDraftAcceptConfirm.loading = true;
      }
      if (modalSource === AcceptEfektikaModalSource.DIP && this.efektikaDraftAcceptDipConfirm) {
        this.efektikaDraftAcceptDipConfirm.loading = true;
      }
      if (modalSource === AcceptEfektikaModalSource.REGULAR_EXCEEDED && this.efektikaDraftAcceptRegularExceedConfirm) {
        this.efektikaDraftAcceptRegularExceedConfirm.loading = true;
      }
    }
  }

  public openDeleteTemplateDraftConfirmModal(input: DeleteDraftTemplateModalConfirm): void {
    this.deleteTemplateDraftConfirm = input;
    this.display.next('open');
  }

  public onDeleteTemplateDraftConfirm(): void {
    this.preloader = true;
    this.restApiService.deleteDraftTemplateDetail(this.deleteTemplateDraftConfirm!.templateId).subscribe({
      next: () => this.templateDraftDeleted(),
      error: (error) => this.templateDraftDeleteError(error),
    });
  }

  public templateDraftDeleted(): void {
    this.toastService.addNotification({
      title: 'toastNotifications.headers.success',
      message: 'toastNotifications.messages.templateDeleted',
      type: 'success',
      component: ToastNotificationComponent,
    });
    this.preloader = false;
    this.router.navigate(['/' + DRAFT_TEMPLATES_ROUTE]);
    this.close();
  }

  public templateDraftDeleteError(error: unknown): void {
    this.loggerService.error(error);
    this.toastService.unknownApiError();
    this.preloader = false;
  }

  public openPortfolioVatConfirm(): void {
    this.signalsService.confirmCreateNewPortfolioVat(false);
    this.newPortfolioVatConfirm = true;
    this.display.next('open');
  }

  public handlePortfolioVatAccept(): void {
    this.signalsService.confirmCreateNewPortfolioVat(true);
    this.close();
  }

  public openOpfTransferModal(selectedClientId: number): void {
    this.opfFeesConfirmClientId = selectedClientId;
    this.display.next('open');
  }

  public confirmOpfTransfer(): void {
    this.preloader = true;
    this.restApiService.allowOpfTransfer(this.opfFeesConfirmClientId ?? NaN).subscribe({
      next: () => this.opfConfirmed(),
      error: (error) => this.opfConfirmError(error),
    });
  }
  public openNewRegularWithdrawModal(input: NewRegularWithdrawal): void {
    this.newRegularWithdrawal = input;
    this.display.next('open');
  }

  public continueToWidthdrawalDraftComment(): void {
    this.router.navigate(['/' + WITHDRAWAL_COMMENT_ROUTE, { queryParams: { steps: STEPPER_LIMITATIONS.STEPS_LIMITED }}]);
    this.close();
  }

  private opfConfirmed(): void {
    this.toastService.addNotification({
      title: 'toastNotifications.headers.success',
      message: 'toastNotifications.messages.opfConfirmed',
      type: 'success',
      component: ToastNotificationComponent,
    });
    this.signalsService.opfAdditionConfirm();
    this.close();
  }

  private opfConfirmError(error: unknown): void {
    this.preloader = false;
    this.loggerService.error(error);
    this.toastService.addNotification({
      title: 'toastNotifications.headers.error',
      message: 'toastNotifications.messages.opfConfirmError',
      type: 'warning',
      component: ToastNotificationComponent,
    });
  }

  public openInitSignIn(): void {
    this.initSignIn = true;
    this.disableClose = false;
    this.preloader = false;
    this.display.next('open');
  }

  public handleInitLogInAddMoney(): void {
    this.close();
    this.router.navigate(['/money/deposit']);
  }

  public handleInitLogInAddPortfolio(): void {
    this.close();
    this.router.navigate(['/portfolio/new']);
  }

  public handleInitLogInAddStrategy(): void {
    this.close();
    this.router.navigate(['/strategy/pick']);
  }

  public get efektikaModalChainingDraftData(): ExpectedOwnedStrategy | null {
    return this.acceptEfektikaDraftModalChaning?.draftPortfolio ?? null;
  }

  public openTransactionsExportModal(): void {
    this.transactionsExportForm = true;
    this.display.next('open');
  }

  public openSaveStrategyTemplateModal(instruments: Record<number, number>): void {
    this.saveStrategyTemplate = { instruments };
    this.saveStrategyTemplateSaving = false;
    this.display.next('open');
  }

  public createSaveStrategyTemplate(payload: SaveStrategyTemplateCreatePayload): void {
    if (!this.saveStrategyTemplate) {
      return;
    }

    const name = payload.name;

    if (!name || name.length < DRAFT_TEMPLATES_CONFIG.nameMinLength) {
      this.toastService.addNotification({
        message: 'toastNotifications.messages.templateNameTooShort',
        type: 'warning',
        component: ToastNotificationComponent,
      });
      return;
    }

    if (name.length > DRAFT_TEMPLATES_CONFIG.nameMaxLength) {
      this.toastService.addNotification({
        message: 'toastNotifications.messages.templateNameTooLong',
        type: 'warning',
        component: ToastNotificationComponent,
      });
      return;
    }

    if ((payload.description?.length ?? 0) > DRAFT_TEMPLATES_CONFIG.descriptionMaxLength) {
      this.toastService.addNotification({
        message: 'toastNotifications.messages.templateDescriptionTooLong',
        type: 'warning',
        component: ToastNotificationComponent,
      });
      return;
    }

    const input: DraftTemplateIn = {
      name: payload.name,
      color: payload.color,
      description: payload.description,
      instruments: this.saveStrategyTemplate.instruments,
    };

    this.saveStrategyTemplateSaving = true;
    this.disableClose = true;

    this.restApiService.createDraftTemplate(input).subscribe({
      error: (error) => this.handleSaveStrategyTemplateError(error),
      next: () => this.handleSaveStrategyTemplateSuccess(),
    });
  }

  private handleSaveStrategyTemplateSuccess(): void {
    this.saveStrategyTemplateSaving = false;
    this.disableClose = false;
    this.toastService.addNotification({
      title: 'toastNotifications.headers.success',
      message: 'toastNotifications.messages.templateCreated',
      type: 'success',
      component: ToastNotificationComponent,
    });
    this.close();
  }

  private handleSaveStrategyTemplateError(error: unknown): void {
    this.saveStrategyTemplateSaving = false;
    this.disableClose = false;
    this.loggerService.error(error);

    if (isInputError(error, 422)) {
      this.toastService.addNotification({
        title: 'toastNotifications.headers.error',
        message: 'toastNotifications.messages.templateNameAlreadyExist',
        type: 'warning',
        component: ToastNotificationComponent,
      });
      return;
    }

    this.toastService.unknownApiError();
  }
}
